import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { AccountSwitcherDropdown } from './AccountSwitcherDropdown';
import { UserStats } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { Menu } from 'lucide-react';
import { AccountsState } from '../../types/accounts';
import { APP_NAME } from '../../config/constants';
interface AppShellRedesignedProps {
  children: React.ReactNode;
  stats: UserStats;
  displayName: string;
  currentView: string;
  onNavigate: (view: string) => void;
  selectedGroup: GroupId;
  onSelectGroup: (group: GroupId) => void;
  accounts?: AccountsState;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
}
export function AppShellRedesigned({
  children,
  stats,
  displayName,
  currentView,
  onNavigate,
  selectedGroup,
  onSelectGroup,
  accounts,
  onSwitchAccount,
  onLogout
}: AppShellRedesignedProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Track if this is the first render to skip the entry animation
  const isFirstRender = useRef(true);
  const skipInitial = isFirstRender.current;
  if (isFirstRender.current) {
    isFirstRender.current = false;
  }
  // Convert AccountsState to array of accounts
  const accountsList = accounts ? Object.values(accounts.accountsById) : [];
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts ? accounts.accountsById[activeAccountId] : null;
  const activeAccountRole = activeAccount?.role || 'learner';
  return <div className="min-h-screen bg-[#FAFAF8] flex font-sans text-slate-900" data-id="element-3794">
      {/* Skip-to-content link for keyboard accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 font-semibold text-sm" data-id="element-3795">
        Ga naar inhoud
      </a>

      {/* Desktop Sidebar */}
      <Sidebar currentView={currentView} onNavigate={onNavigate} stats={stats} displayName={displayName} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} selectedGroup={selectedGroup} onSelectGroup={onSelectGroup} activeAccountRole={activeAccountRole} data-id="element-3796" />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden relative" data-id="element-3797">
        {/* Mobile Header - IMPROVED: Better padding and sizing */}
        <header className="md:hidden h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-5 sticky top-0 z-30 shadow-sm" data-id="element-3798">
          <div className="flex items-center gap-3" data-id="element-3799">
            <button onClick={() => setIsMobileNavOpen(true)} className="p-3 -ml-2 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-indigo-300" aria-label="Menu openen" data-id="element-3800">
              <Menu className="w-6 h-6" data-id="element-3801" />
            </button>
            <span className="font-black text-slate-800 text-lg tracking-tight" data-id="element-3802">
              {APP_NAME}
            </span>
          </div>

          <div className="flex items-center gap-3" data-id="element-3803">
            {/* Only show group selector for learners */}
            {activeAccountRole === 'learner' && <div className="px-3 py-1.5 bg-indigo-100 rounded-full text-xs font-bold text-indigo-700 shadow-sm border border-indigo-200" data-id="element-3804">
                Groep {selectedGroup}
              </div>}
            {/* Mobile Account Switcher */}
            {accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-3805" />}
          </div>
        </header>

        {/* Desktop Header with Account Switcher - IMPROVED: Better height */}
        <header className="hidden md:flex h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 items-center justify-end px-8 sticky top-0 z-30 shadow-sm" data-id="element-3806">
          {accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-3807" />}
        </header>

        {/* Scrollable Content - IMPROVED: Better padding */}
        <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10 scroll-smooth" data-id="element-3808">
          <div className="max-w-6xl mx-auto w-full pb-24 md:pb-12" data-id="element-3809">
            <AnimatePresence mode="wait" data-id="element-3810">
              <motion.div key={currentView} initial={skipInitial ? false : {
              opacity: 0,
              y: 10,
              scale: 0.99
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -10,
              scale: 0.99
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="w-full" data-id="element-3811">
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Mobile Navigation Drawer */}
      <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} currentView={currentView} onNavigate={onNavigate} stats={stats} displayName={displayName} selectedGroup={selectedGroup} onSelectGroup={onSelectGroup} activeAccountRole={activeAccountRole} onLogout={onLogout} data-id="element-3812" />
    </div>;
}